import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  ButtonVariant,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  Link,
} from '@gousto-internal/citrus-react'
import type { DashboardExperienceOrderResponse } from '@library/api-dashboard-experience'
import { browserHistory } from 'react-router'
import { useOrderTracking } from '../../../hooks/useOrderTracking'

import { RecommendationRecipeCard } from '../RecommendationRecipeCard/RecommendationRecipeCard'
import { useRecommendationCarouselNav } from './useRecommendationCarouselNav'

export type RecommendationSectionData = {
  collection: 'top picks for you' | 'new and limited edition'
  id: string
  seeMoreLabel?: string
  title: string
  recipes: DashboardExperienceOrderResponse['recipes']
}

type RecommendationSectionProps = {
  orderId: string
  section: RecommendationSectionData
}

export const RecommendationSection = ({ orderId, section }: RecommendationSectionProps) => {
  const { trackCarouselScroll, trackCarouselSeeMoreClick, trackRecommendedRecipeClick } =
    useOrderTracking()
  const { emblaRef, nextBtnDisabled, onNextButtonClick, onPrevButtonClick, prevBtnDisabled } =
    useRecommendationCarouselNav({
      hasSeeMoreCard: Boolean(section.seeMoreLabel),
    })

  const orderMenuPath = `/menu/${orderId}`

  const handleRecipeClick = () => {
    trackRecommendedRecipeClick({
      orderId,
      collection: section.collection,
    })
    browserHistory.push(orderMenuPath)
  }

  const handleSeeMoreClick = () => {
    trackCarouselSeeMoreClick({
      orderId,
      collection: section.collection,
    })
    browserHistory.push(orderMenuPath)
  }

  const handlePrevButtonClick = () => {
    trackCarouselScroll({
      orderId,
      collection: section.collection,
    })
    onPrevButtonClick()
  }

  const handleNextButtonClick = () => {
    trackCarouselScroll({
      orderId,
      collection: section.collection,
    })
    onNextButtonClick()
  }

  const prevButtonDisplay = prevBtnDisabled ? 'none' : 'flex'
  const nextButtonDisplay = nextBtnDisabled ? 'none' : 'flex'

  return (
    <Box position={DisplayPosition.Relative}>
      <Body2
        color={Color.ColdGrey_900}
        fontWeight={FontWeight.Bold}
        style={{ marginBottom: '12px' }}
      >
        {section.title}
      </Body2>
      <Box
        className="embla__viewport"
        ref={emblaRef}
        width="100%"
        style={{
          overflow: 'hidden',
        }}
      >
        <Box
          aria-label={section.title}
          alignItems={AlignItems.Stretch}
          className="embla__container"
          display={Display.Flex}
          gap={3}
          role="list"
          style={{
            paddingBottom: '4px',
          }}
        >
          {section.recipes.map((recipe) => (
            <Box
              key={recipe.id}
              className="embla__slide"
              display={Display.Flex}
              flexGrow={0}
              flexShrink={0}
            >
              <RecommendationRecipeCard onClick={handleRecipeClick} recipe={recipe} />
            </Box>
          ))}
          {section.seeMoreLabel ? (
            <Box
              className="embla__slide"
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              gap={2}
              alignItems={AlignItems.Center}
              justifyContent={JustifyContent.Center}
              onClick={handleSeeMoreClick}
              style={{ minWidth: '135px' }}
            >
              <Icon
                name="arrow_right"
                variant={IconVariant.Actionable}
                style={{ cursor: 'pointer' }}
              />
              <Link href={orderMenuPath}>{section.seeMoreLabel}</Link>
            </Box>
          ) : null}
        </Box>
      </Box>
      <Button
        aria-label="Previous slide"
        disabled={prevBtnDisabled}
        className="embla__prev"
        colorVariant={ButtonColorVariant.Secondary}
        icon="arrow_back"
        onClick={handlePrevButtonClick}
        size={ButtonSize.Large}
        style={{
          borderRadius: '50%',
          display: prevButtonDisplay,
          position: 'absolute',
          bottom: '40%',
          left: '1%',
        }}
        variant={ButtonVariant.Icon}
      />
      <Button
        aria-label="Next slide"
        disabled={nextBtnDisabled}
        className="embla__next"
        colorVariant={ButtonColorVariant.Secondary}
        icon="arrow_right"
        onClick={handleNextButtonClick}
        size={ButtonSize.Large}
        style={{
          borderRadius: '50%',
          display: nextButtonDisplay,
          position: 'absolute',
          bottom: '40%',
          right: '1%',
        }}
        variant={ButtonVariant.Icon}
      />
    </Box>
  )
}
